import React from "react";
import { Link } from "react-router-dom";
import { MarketingHeader } from "@/components/Header";
import { Feather, Type, Layers, FileDown, KeyRound, ShieldCheck } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--canvas)] relative">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center relative">
          <div className="md:col-span-7 relative z-10">
            <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-6" data-testid="hero-tagline">
              For writers who actually want to write
            </p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[var(--ink-primary)]" data-testid="hero-headline">
              Stop fighting your formatting.<br />
              <span className="italic text-[var(--brand)]">Start writing</span> your story.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[var(--ink-secondary)] leading-relaxed max-w-2xl" data-testid="hero-sub">
              Type the way you think. The second you press Enter, your scene headings, characters,
              dialogue and transitions snap into industry-standard format — automatically. No menus.
              No formatting toolbar dance. Just the page and the work.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup" className="btn-brand text-base" data-testid="hero-cta-primary">Start writing — 7 days free</Link>
              <Link to="/login" className="btn-ghost text-base" data-testid="hero-cta-secondary">I already have an account</Link>
            </div>
            <p className="mt-4 text-xs text-[var(--ink-muted)]" data-testid="hero-fineprint">No credit card. Cancel any time. Your work is always yours to download.</p>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1740131618025-43c0ad919563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMHdyaXRlciUyMGRlc2slMjBvdmVyaGVhZHxlbnwwfHx8fDE3ODA2ODQ1MjB8MA&ixlib=rb-4.1.0&q=85"
                alt="Writer's desk"
                className="rounded-md shadow-xl border border-[var(--line)]"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 md:-left-12 bg-white shadow-lg border border-[var(--line)] rounded-md p-5 max-w-[280px] hidden md:block">
                <div className="font-mono-script text-[12px] leading-snug text-[#111]">
                  <div className="font-bold uppercase mb-1.5">EXT. SUBURBAN HOME — NIGHT</div>
                  <div>A boy in a knight's costume runs the length of the front porch, sword raised.</div>
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">
                  <span className="inline-block w-2 h-2 rounded-full bg-[var(--ok)] mr-1.5 align-middle" />
                  formatted automatically
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="py-20 md:py-28 bg-[var(--canvas)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-3">What's inside</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight" data-testid="features-headline">
              Built for the way you actually write.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <article key={f.title} className="bg-white border border-[var(--line)] rounded-md p-8 hover:shadow-md transition-shadow" data-testid={`feature-${f.id}`}>
                <f.Icon size={22} strokeWidth={1.5} className="text-[var(--brand)] mb-5" />
                <h3 className="font-display text-2xl mb-3 leading-tight">{f.title}</h3>
                <p className="text-[var(--ink-secondary)] text-sm leading-relaxed">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT FEATURE — focus */}
      <section className="py-20 md:py-28 bg-[var(--warm-accent)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <img
              src="https://images.unsplash.com/photo-1510442650500-93217e634e4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMHBlcnNvbiUyMHR5cGluZyUyMGZvY3VzZWR8ZW58MHx8fHwxNzgwNjg0NTIwfDA&ixlib=rb-4.1.0&q=85"
              alt="Person typing"
              className="rounded-md shadow-xl border border-[var(--line)]"
              data-testid="focus-image"
            />
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-secondary)] mb-3">The point</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mb-6">
              The page does the bookkeeping.<br />
              <span className="italic text-[var(--brand)]">You do the writing.</span>
            </h2>
            <p className="text-[var(--ink-secondary)] text-base md:text-lg leading-relaxed">
              You type "Ext. Suburban Home — Night" and keep going. By the time your fingers
              hit the next word, the page has already centered the scene heading, made it bold,
              numbered the scene, and indented the action below. No lag. No menu hunting.
              Just you and the next line.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-3" data-testid="pricing-eyebrow">Plain old pricing</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight">
              One plan. One price. <span className="italic">No nonsense.</span>
            </h2>
          </div>
          <div className="max-w-md mx-auto bg-white border border-[var(--line)] rounded-lg p-10 text-center shadow-sm" data-testid="pricing-card">
            <h3 className="font-display text-3xl mb-2">Writer</h3>
            <div className="font-display text-6xl mb-1 text-[var(--brand)]" data-testid="pricing-amount">$19.97<span className="text-2xl text-[var(--ink-secondary)] font-ui">/mo</span></div>
            <p className="text-[var(--ink-muted)] text-sm mb-8">7 days free. No card required.</p>
            <ul className="text-left text-sm text-[var(--ink-secondary)] space-y-3 mb-10">
              <li>· Unlimited scripts</li>
              <li>· Real-time scene/character/dialogue auto-formatting</li>
              <li>· Resources panel: characters, beats, outline, notes</li>
              <li>· Export to PDF, Final Draft FDX, and Fountain</li>
              <li>· Cancel any time</li>
            </ul>
            <Link to="/signup" className="btn-brand inline-block w-full text-base" data-testid="pricing-cta">Start writing</Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[var(--line)] py-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-display text-lg">New <span className="text-[var(--brand)]">·</span> Screenplay</p>
          <p className="text-xs text-[var(--ink-muted)]">Written by humans. Formatted by us.</p>
        </div>
      </footer>
    </div>
  );
}

const FEATURES = [
  {
    id: "auto-format",
    Icon: Type,
    title: "Auto-format on Enter",
    body: "Type a scene heading, an action, a character name, a line of dialogue — the page recognizes what you wrote and formats it the moment you press Enter. Override anything with one click.",
  },
  {
    id: "resources",
    Icon: Layers,
    title: "Resources at your side",
    body: "Pop open a character card, a moodboard image, a stack of notes, or your beat sheet — without ever leaving the page you're writing.",
  },
  {
    id: "export",
    Icon: FileDown,
    title: "Export to anything",
    body: "Industry-formatted PDF for your meeting. .fdx for your agent. .fountain for your future self. All from one menu.",
  },
  {
    id: "focus",
    Icon: Feather,
    title: "Built for focus",
    body: "Anti-distraction by design. The page in the middle. Resources on the side. Nothing flashing for your attention.",
  },
  {
    id: "yours",
    Icon: KeyRound,
    title: "Your work is yours",
    body: "You can export your scripts at any time, even if your subscription lapses. We never hold your work hostage.",
  },
  {
    id: "trust",
    Icon: ShieldCheck,
    title: "Boring billing",
    body: "$19.97/mo after the 7-day trial. We email you before charging. Cancel from your account in two clicks.",
  },
];
